import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UnitAvailability } from '@brimo/shared';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types and query hooks of the reservations routes (docs/dev/api-reservations.md). */

export type ReservationStatus =
  'awaiting_signature' | 'awaiting_approval' | 'active' | 'rejected' | 'withdrawn' | 'released';

export type UnitStatus = 'available' | 'reserved' | 'sold' | 'unavailable';

export interface ReservationUserRef {
  id: string;
  name: string;
  initials: string;
}

export interface ReservationUnit {
  id: string;
  number: string;
  type: 'apartment' | 'parking_space' | 'commercial' | 'other';
  propertyId: string;
  propertyName: string;
  building: string | null;
  floor: string | null;
  areaSqm: number | null;
  rooms: number | null;
  priceCents: number | null;
  parkingSpace: { number: string; priceCents: number | null } | null;
  serviceChargeCents: number | null;
  status: UnitStatus;
}

export type ApprovalCheckKey = 'signed' | 'self_disclosure' | 'unit_was_available' | 'financier';

export interface ApprovalCheck {
  key: ApprovalCheckKey;
  label: string;
  detail: string;
  state: 'done' | 'open';
}

export interface ReservationApproval {
  status: 'open' | 'approved' | 'rejected' | 'withdrawn';
  approvalId: string | null;
  byShort: string | null;
  at: string | null;
  note: string | null;
  rejectionReason: string | null;
  rejectionNote: string | null;
  checks: ApprovalCheck[];
}

export interface ReservationHistoryEntry {
  at: string;
  title: string;
  byShort: string | null;
}

export interface ReservationItem {
  id: string;
  status: ReservationStatus;
  statusLabel: string;
  unit: ReservationUnit;
  contact: { id: string; name: string; email: string | null };
  deal: { id: string; number: number; stageName: string; stageSystemKey: string | null };
  startedBy: ReservationUserRef;
  startedAt: string;
  advisor: ReservationUserRef | null;
  messageToAdvisor: string | null;
  signatureMethod: 'link' | 'on_site';
  sentAt: string | null;
  linkValidUntil: string | null;
  linkExpired: boolean;
  openedAt: string | null;
  signedAt: string | null;
  signaturePlace: string | null;
  aborted: { at: string; reason: string | null } | null;
  approval: ReservationApproval | null;
  released: {
    at: string;
    byShort: string | null;
    reason: string;
    reasonLabel: string;
    note: string | null;
  } | null;
  withdrawn: { at: string; byShort: string | null; reason: string | null } | null;
  undoUntil: string | null;
  escalation: { level: number; remindedAt: string | null; nextStep: string; unsignedDays: number };
  deliveries: Array<{ name: string; status: 'wartet' | 'gesendet' }>;
  agreementPdfFileId: string | null;
  releaseRequest: {
    approvalId: string;
    requestedByShort: string | null;
    at: string;
    reason: string | null;
  } | null;
  canWithdraw: boolean;
  canRelease: boolean;
  canRequestRelease: boolean;
  canRemind: boolean;
  canApprove: boolean;
  canSign: boolean;
  canUndo: boolean;
  releaseBlockedHelper: string | null;
  history: ReservationHistoryEntry[];
  options: { releaseReasons: string[]; rejectionReasons: string[] };
}

export interface ContactHit {
  contactId: string;
  name: string;
  email: string | null;
  dealLabel: string;
  selfDisclosure: { label: string };
  financing: string;
}

export interface ReservationContext {
  unit: {
    id: string;
    number: string;
    propertyId: string;
    propertyName: string;
    areaSqm: number | null;
    priceCents: number | null;
    parkingSpace: { number: string; priceCents: number | null } | null;
    serviceChargeCents: number | null;
    status: UnitStatus;
  };
  hits: ContactHit[];
  selected: ContactHit | null;
  agreementPreviewUrl: string;
  canReserve: boolean;
  blockedReason: string | null;
  mode: 'reserve' | 'request';
  reservationId: string | null;
  advisors: Array<{ id: string; name: string }>;
}

export interface AlternativeUnit {
  unitId: string;
  number: string;
  floor: string | null;
  areaSqm: number | null;
  rooms: number | null;
  priceCents: number | null;
}

/** `details` of the 409 „Die Einheit ist bereits reserviert." (Reservierung 02). */
export interface BlockedDetails {
  alternatives: AlternativeUnit[];
  blocking: {
    reservationId: string;
    status: ReservationStatus;
    since: string;
    advisorShort: string | null;
    approvedByShort: string | null;
  } | null;
}

export interface StartReservationBody {
  unitId: string;
  contactId: string;
  signatureMethod: 'link' | 'on_site';
  sendAgreement?: boolean;
  advisorId?: string;
  messageToAdvisor?: string;
}

export type StartReservationResponse = ReservationItem & { link: string | null };

/** The subset of `UnitDetail` (docs/dev/api-properties.md) the dialog reads for the unit row. */
export interface UnitDetailSummary {
  id: string;
  number: string;
  propertyName: string;
  data: { buildingFloor: string; rooms: string; area: string };
}

/** A unit row of `GET /properties/:id/units` (docs/dev/api-properties.md) — the alternatives of a blocked unit. */
export interface PropertyUnitRow {
  id: string;
  number: string;
  status: UnitStatus;
  floor: string | null;
  rooms: number | null;
  areaSqm: number | null;
  priceCents: number | null;
  reservation: { id: string } | null;
  /** status × sales release × TeamProQ block; only `available` can be reserved. */
  availability: UnitAvailability;
}

export const reservationKeys = {
  detail: (id: string) => [...queryRoots.reservations, 'detail', id] as const,
  list: (params: Record<string, string>) => [...queryRoots.reservations, 'list', params] as const,
  context: (unitId: string, q: string, contactId: string | null) =>
    [...queryRoots.reservations, 'context', unitId, q, contactId] as const,
  unit: (unitId: string) => [...queryRoots.units, 'detail', unitId] as const,
  propertyUnits: (propertyId: string) => [...queryRoots.units, 'list', propertyId, 'available'] as const,
};

export function useReservation(id: string | null) {
  return useQuery({
    queryKey: reservationKeys.detail(id ?? ''),
    queryFn: ({ signal }) => api<ReservationItem>(`/reservations/${id ?? ''}`, { signal }),
    enabled: id !== null,
    retry: (count, error) =>
      !(error instanceof ApiError && (error.status === 403 || error.status === 404)) && count < 1,
  });
}

export function useReservationContext(unitId: string, q: string, contactId: string | null, enabled: boolean) {
  const search = new URLSearchParams();
  if (q) search.set('q', q);
  if (contactId) search.set('contactId', contactId);
  const suffix = search.toString();
  return useQuery({
    queryKey: reservationKeys.context(unitId, q, contactId),
    queryFn: ({ signal }) =>
      api<ReservationContext>(`/units/${unitId}/reservation-context${suffix ? `?${suffix}` : ''}`, {
        signal,
      }),
    enabled,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

export function useUnitDetail(unitId: string, enabled: boolean) {
  return useQuery({
    queryKey: reservationKeys.unit(unitId),
    queryFn: ({ signal }) => api<UnitDetailSummary>(`/units/${unitId}`, { signal }),
    enabled,
    retry: false,
  });
}

export function useAvailableUnits(propertyId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: reservationKeys.propertyUnits(propertyId ?? ''),
    queryFn: ({ signal }) =>
      api<{ items: PropertyUnitRow[] }>(`/properties/${propertyId ?? ''}/units?status=available&limit=100`, {
        signal,
      }),
    enabled: enabled && propertyId !== null,
    retry: false,
  });
}

/** Refreshes everything a reservation change touches: the reservation, the unit, the deal, the approvals. */
export function useInvalidateReservations() {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all(
      [
        queryRoots.reservations,
        queryRoots.units,
        queryRoots.property,
        queryRoots.properties,
        queryRoots.deal,
        queryRoots.deals,
        queryRoots.approvals,
        queryRoots.tasks,
        queryRoots.contact,
        queryRoots.session,
      ].map((key) => queryClient.invalidateQueries({ queryKey: [...key] })),
    );
  };
}

export function startReservation(body: StartReservationBody): Promise<StartReservationResponse> {
  return api<StartReservationResponse>('/reservations', { method: 'POST', body });
}

export function signReservation(id: string, body: { signaturePng: string; place: string }) {
  return api<ReservationItem>(`/reservations/${id}/sign`, { method: 'POST', body });
}

export function remindReservation(id: string) {
  return api<ReservationItem>(`/reservations/${id}/remind`, { method: 'POST', body: {} });
}

export function withdrawReservation(id: string, reason?: string) {
  return api<ReservationItem>(`/reservations/${id}/withdraw`, {
    method: 'POST',
    body: reason === undefined ? {} : { reason },
  });
}

export function releaseReservation(id: string, body: { reason: string; note?: string }) {
  return api<ReservationItem>(`/reservations/${id}/release`, { method: 'POST', body });
}

export function undoReservation(id: string) {
  return api<ReservationItem>(`/reservations/${id}/undo`, { method: 'POST', body: {} });
}

export function requestRelease(id: string, reason: string) {
  return api<{ approvalId: string }>(`/reservations/${id}/release-request`, {
    method: 'POST',
    body: { reason },
  });
}

export function rejectReleaseRequest(id: string, body: { reason: string; note?: string }) {
  return api<ReservationItem>(`/reservations/${id}/release-request/reject`, { method: 'POST', body });
}

export function approveReservation(id: string, note?: string) {
  return api<ReservationItem>(`/reservations/${id}/approve`, {
    method: 'POST',
    body: note === undefined || note === '' ? {} : { note },
  });
}

export function rejectReservation(id: string, body: { reason: string; note: string }) {
  return api<ReservationItem>(`/reservations/${id}/reject`, { method: 'POST', body });
}

export function useSignReservation() {
  const invalidate = useInvalidateReservations();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { signaturePng: string; place: string } }) =>
      signReservation(id, body),
    onSuccess: invalidate,
  });
}

/** The 409 of `POST /reservations` with the alternatives of Reservierung 02, or null for any other error. */
export function blockedDetailsOf(error: unknown): BlockedDetails | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const alternatives = error.details?.['alternatives'];
  if (!Array.isArray(alternatives)) return null;
  const blocking = error.details?.['blocking'];
  return {
    alternatives: alternatives as AlternativeUnit[],
    blocking:
      typeof blocking === 'object' && blocking !== null ? (blocking as BlockedDetails['blocking']) : null,
  };
}

/** The URL of the stored/preview agreement PDF of a reservation (opens in a new tab). */
export function agreementPdfHref(reservationId: string): string {
  return `/api/reservations/${reservationId}/agreement.pdf`;
}
